import { useCallback } from 'react';
import { MapPin } from 'lucide-react';
import type { PdpDeliveryPresentationVm } from '../viewModels/pdpDelivery.vm';
import { getSameDayDeliveryLabel } from '../viewModels/pdpDelivery.vm';
import { PdpStoreAvailability } from './PdpStoreAvailability';

interface PdpDeliveryInfoProps {
  inputValue: string;
  pincodeError: string | null;
  deliveryError: string | null;
  isLoadingDelivery: boolean;
  delivery: PdpDeliveryPresentationVm;
  designName: string;
  productUrl: string;
  onInputChange: (value: string) => void;
  onUpdate: () => Promise<boolean>;
  onViewAllDesigns: () => void;
  onViewInventory: () => void;
  onPincodeInputChange?: (pincode: string) => void;
  onPincodeUpdateClick?: (pincode: string) => void;
}

export function PdpDeliveryInfo({
  inputValue,
  pincodeError,
  deliveryError,
  isLoadingDelivery,
  delivery,
  designName,
  productUrl,
  onInputChange,
  onUpdate,
  onViewAllDesigns,
  onViewInventory,
  onPincodeInputChange,
  onPincodeUpdateClick,
}: PdpDeliveryInfoProps) {
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onInputChange(e.target.value);
      onPincodeInputChange?.(e.target.value);
    },
    [onInputChange, onPincodeInputChange]
  );

  const handleUpdate = useCallback(() => {
    onPincodeUpdateClick?.(inputValue);
    void onUpdate();
  }, [inputValue, onPincodeUpdateClick, onUpdate]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        handleUpdate();
      }
    },
    [handleUpdate]
  );

  const sameDayLabel = getSameDayDeliveryLabel(delivery.sameDayMessage);
  const displayError = pincodeError ?? deliveryError;
  const displayPincode = delivery.pincode || inputValue;

  return (
    <div className="border-t border-gray-200 px-4 py-4 md:border-t-0 md:bg-white md:px-4 md:py-4 md:mb-4">
      <div className="flex items-start items-center gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <MapPin className="h-5 w-5 shrink-0 text-gray-900" strokeWidth={1.75} aria-hidden />
          <span className="text-sm text-gray-900">
            <span className="hidden sm:inline">Your </span>Pincode
          </span>
        </div>

        <div className="relative flex w-[170px] shrink-0 overflow-hidden rounded-md border border-gray-300 bg-white">
          <input
            type="tel"
            inputMode="numeric"
            pattern="[0-9]{6}"
            maxLength={6}
            placeholder="Pincode"
            value={inputValue}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            className="w-full border-0 bg-transparent py-2.5 pl-3 pr-[72px] text-sm outline-none placeholder:text-gray-400"
          />
          <button
            type="button"
            onClick={handleUpdate}
            disabled={isLoadingDelivery}
            className="absolute right-0 top-0 flex h-full w-[80px] items-center justify-center border-l border-gray-300 text-sm text-[var(--link-blue)] hover:bg-gray-50 disabled:opacity-50"
          >
            {isLoadingDelivery ? 'Updating…' : 'Update'}
          </button>
        </div>
      </div>

      {displayError && <p className="mt-2 text-xs text-orange-500">{displayError}</p>}

      <PdpStoreAvailability
        delivery={delivery}
        designName={designName}
        productUrl={productUrl}
        onViewAllDesigns={onViewAllDesigns}
        onViewInventory={onViewInventory}
      />

      {delivery.hasDeliveryData && delivery.edd ? (
        <div className="mt-3">
          <p className="text-sm leading-4 text-gray-900">
            <span className="whitespace-nowrap">
              Delivery by <strong>{delivery.edd}</strong>
            </span>
            {displayPincode && (
              <>
                {' '}
                <span className="whitespace-nowrap">
                  for <strong>Pincode {displayPincode}</strong>
                </span>
              </>
            )}
          </p>
          {sameDayLabel && (
            <p className="mt-1.5 text-xs text-gray-500">
              (Order before 4pm for {sameDayLabel})
            </p>
          )}
        </div>
      ) : (
        !isLoadingDelivery && (
          <p className="mt-3 text-sm text-gray-900">
            Provide pincode for delivery date &amp; nearby stores!
          </p>
        )
      )}
    </div>
  );
}
